// logger.js — Barakah Budget structured logging
//
// Usage:
//   import { logInfo, logError, logWarn } from "./logger";
//   logInfo("transactions", "add_tx", { amount: 25, category: "Food" });
//   logError("storage", "read_failed", error);
//
// Categories: auth | budget | transactions | storage | pro_gating | onboarding | ai | analytics
//
// To add Sentry later: replace the body of _send() only — nothing else changes.
// To add LogRocket:    same — replace _send() body only.

// ── CONFIG ──────────────────────────────────────────────────
const IS_DEV  = process.env.NODE_ENV === "development";
const VERSION = process.env.REACT_APP_VERSION || "dev";

// In production: only warn + error. In dev: everything.
const LOG_LEVELS = { info: 0, warn: 1, error: 2 };
const MIN_LEVEL  = IS_DEV ? LOG_LEVELS.info : LOG_LEVELS.warn;

// ── INTERNAL SEND ────────────────────────────────────────────
// Single place to swap in Sentry, LogRocket, or any other service.
function _send(level, category, event, data) {
  if (LOG_LEVELS[level] < MIN_LEVEL) return;

  const entry = {
    ts:       new Date().toISOString(),
    level,
    category,
    event,
    version:  VERSION,
    ...(data ? { data } : {}),
  };

  const prefix = `[BB:${category}] ${event}`;

  if (level === "error") {
    console.error(prefix, entry);
    // Future Sentry: Sentry.captureMessage(event, { level: "error", extra: entry });
  } else if (level === "warn") {
    console.warn(prefix, entry);
    // Future Sentry: Sentry.captureMessage(event, { level: "warning", extra: entry });
  } else {
    console.log(prefix, entry);
  }

  // Future remote logging:
  // fetch("/api/logs", { method: "POST", body: JSON.stringify(entry) });
}

// ── PUBLIC API ───────────────────────────────────────────────

/**
 * Log a normal successful operation.
 * @param {string} category  auth | budget | transactions | storage | pro_gating | onboarding | ai | analytics
 * @param {string} event     snake_case name e.g. "add_tx_success"
 * @param {object} [data]    optional context — no PII, no raw amounts in production
 */
export function logInfo(category, event, data) {
  _send("info", category, event, data);
}

/**
 * Log something unexpected that the app recovered from.
 * @param {string} category
 * @param {string} event
 * @param {object|Error} [data]
 */
export function logWarn(category, event, data) {
  _send("warn", category, event, data instanceof Error ? {
    message: data.message,
    stack:   IS_DEV ? data.stack : undefined,
  } : data);
}

/**
 * Log something that failed and the user may have noticed.
 * @param {string} category
 * @param {string} event
 * @param {Error|object} [err]
 */
export function logError(category, event, err) {
  _send("error", category, event, err instanceof Error ? {
    message: err.message,
    name:    err.name,
    stack:   IS_DEV ? err.stack : undefined,
  } : err);
}

// ── GLOBAL ERROR HANDLERS ────────────────────────────────────
// Call setupGlobalErrorHandlers() once in index.js.
// Catches errors that escape React's error boundary.

export function setupGlobalErrorHandlers() {
  // Uncaught JS errors
  window.addEventListener("error", (event) => {
    logError("uncaught", "window_error", {
      message:  event.message,
      filename: event.filename,
      line:     event.lineno,
      col:      event.colno,
      stack:    IS_DEV ? event.error?.stack : undefined,
    });
  });

  // Unhandled promise rejections (failed fetch, async errors, etc.)
  window.addEventListener("unhandledrejection", (event) => {
    logError("uncaught", "unhandled_promise", {
      message: event.reason?.message || String(event.reason),
      stack:   IS_DEV ? event.reason?.stack : undefined,
    });
    event.preventDefault(); // suppress duplicate browser console noise
  });

  logInfo("app", "global_handlers_ready", { version: VERSION, env: IS_DEV ? "dev" : "prod" });
}

// ── STORAGE HELPERS ──────────────────────────────────────────
// Logged wrappers for localStorage. Use these instead of raw localStorage
// so failures are always visible and traceable.

export function safeRead(key, fallback = null) {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw) ?? fallback;
  } catch (err) {
    logError("storage", "read_failed", { key, message: err.message });
    return fallback;
  }
}

export function safeWrite(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (err) {
    logError("storage", "write_failed", {
      key,
      message: err.message,
      // QuotaExceededError = storage full — user needs to clear data
      quota: err.name === "QuotaExceededError" ? "storage_full" : "unknown",
    });
    return false;
  }
}